import React, { useEffect, useContext, useState } from "react";
import {
  Typography,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  FormControl,
} from "@mui/material";
import { getColors } from "../../themes/theme";
import { ThemeContext } from "../../context/ThemeContext";
import { useForm } from "react-hook-form";
import { SelectImageDialog } from "./SelectImageDialog";
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import axios from "axios";
import { ExerciseOriginItem } from "./ExerciseOriginItem";
import { TreatmentOriginItem } from "./TreatmentOriginItem";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { EditTreatmentsDialog } from "./EditTreatmentsDialog";
import { EditExercisesDialog } from "./EditExercisesDialog";
import { useSnackbar } from "notistack";

export const AddConditionDialog = ({ open, onClose, onAdd }) => {
  const methods = useForm({
    area: "",
    acondition: "",
    description: "",
    bmname: "default",
  });
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;
  const { darkMode } = useContext(ThemeContext);
  const { token } = useAuthContext();
  const [openImageDialog, setOpenImageDialog] = useState(false); // State to manage image dialog visibility
  const [selectedImage, setSelectedImage] = useState("default");
  const [exercises, setExercises] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [allTreatments, setAllTreatments] = useState([]);
  const [openTreatmentsDialog, setOpenTreatmentsDialog] = useState(false);
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState([]);
  const [openExercisesDialog, setOpenExercisesDialog] = useState(false);
  const [selectedExerciseIds, setSelectedExerciseIds] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const handleImageSelect = (bmname) => {
    setSelectedImage(bmname); // Update the selected image
    setValue("bmname", bmname); // Update the form value
  };

  useEffect(() => {
    setValue("area", "");
    setValue("acondition", "");
    setValue("description", "");
    setValue("bmname", "default");
    setSelectedImage("default");
  }, [open, setValue]);

  useEffect(() => {
    const fetchAllExercises = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/exercise`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setAllExercises(response.data);
      } catch (err) {
        console.error("Error fetching all exercises:", err);
      }
    };

    const fetchAllTreatments = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/treatment`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setAllTreatments(response.data);
      } catch (err) {
        console.error("Error fetching all treatments:", err);
      }
    };

    if (token) {
      fetchAllExercises();
      fetchAllTreatments();
    }
  }, [token]);

  let img;
  if (selectedImage !== null && selectedImage !== undefined) {
    img = require(`../../assets/condition/${selectedImage}.png`);
  }

  const onSubmit = (data) => {
    const newConditionData = {
      area: data.area,
      acondition: data.acondition,
      description: data.description,
      bmname: data.bmname,
      practiceid: null,
      treatmentlist:
        selectedTreatmentIds.length > 0
          ? "," + treatments.map((treatment) => treatment.id).join(",")
          : null,
      exerciselist:
        selectedExerciseIds.length > 0
          ? "," + exercises.map((exercise) => exercise.id).join(",")
          : null,
    };
    if (data.bmname === "default")
      enqueueSnackbar("Select Condition Image", { variant: "error" });
    if (selectedTreatmentIds.length === 0)
      enqueueSnackbar("Select at least one Treatment!", { variant: "error" });
    if (selectedExerciseIds.length === 0)
      enqueueSnackbar("Select at least one Exercise!", { variant: "error" });

    if (
      selectedExerciseIds.length > 0 &&
      selectedTreatmentIds.length > 0 &&
      data.bmname !== "default"
    ) {
      onAdd(newConditionData);
      onClose();
    }
  };

  const handleExerciseClicked = () => {
    setOpenExercisesDialog(true);
  };

  const handleTreatmentClicked = () => {
    setOpenTreatmentsDialog(true);
  };

  const handleSaveExercises = (updatedExerciseIds) => {
    setSelectedExerciseIds(updatedExerciseIds);
    const updatedExercises = allExercises.filter((exercise) =>
      updatedExerciseIds.includes(exercise.id)
    );
    setExercises(updatedExercises);
  };

  const handleSaveTreatments = (updatedTreatmentIds) => {
    setSelectedTreatmentIds(updatedTreatmentIds);
    const updatedTreatments = allTreatments.filter((treatment) =>
      updatedTreatmentIds.includes(treatment.id)
    );
    setTreatments(updatedTreatments);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogTitle sx={{ fontSize: "30px", pb: 0 }}>
          Add Condition
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ pt: 0 }}>
            <Box
              display="grid"
              gridTemplateColumns="repeat(2, 1fr)"
              columnGap={3}
            >
              <FormControl fullWidth margin="normal">
                <TextField
                  label="Area Name"
                  placeholder="Enter Area Name"
                  {...register("area", {
                    required: "Area Name is required",
                  })}
                  error={!!errors.area}
                  helperText={errors.area?.message}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <TextField
                  label="Condition Name"
                  placeholder="Enter Condition Name"
                  {...register("acondition", {
                    required: "Condition Name is required",
                  })}
                  error={!!errors.acondition}
                  helperText={errors.acondition?.message}
                />
              </FormControl>
            </Box>

            <Box display="flex" sx={{ mt: 3 }}>
              <img
                src={img}
                alt="img"
                style={{ width: "300px", height: "200px", cursor: "pointer" }}
                onClick={() => setOpenImageDialog(true)} // Open the image selection dialog
              />
              <FormControl fullWidth margin="normal" sx={{ ml: 4 }}>
                <TextField
                  label="Description"
                  multiline
                  rows={4}
                  placeholder="Condition Description"
                  {...register("description", {
                    required: "Description is required",
                  })}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              </FormControl>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              sx={{ mt: 3, alignItems: "center" }}
            >
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: 500,
                  color: darkMode
                    ? getColors.drawerTextDark
                    : getColors.thumbnailTextLight,
                }}
              >
                Treatments
              </Typography>
              <Button
                variant="contained"
                startIcon={
                  <ChangeCircleOutlinedIcon
                    style={{ fontSize: 28, marginRight: 8 }}
                  />
                }
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "20px",
                  px: 4,
                  py: 1,
                  background: darkMode ? "#2D2E30" : "#E6E6E6",
                  color: darkMode
                    ? getColors.primaryTextDark
                    : getColors.secondaryTextLight,
                  "&:hover": {
                    color: getColors.primaryTextDark,
                  },
                }}
                onClick={handleTreatmentClicked}
              >
                Change
              </Button>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={2}
              alignItems={"center"}
              sx={{ mt: 3 }}
            >
              {treatments.map((treatment, id) => {
                // const treatment = treatments.find((t) => t.id === id);
                return <TreatmentOriginItem key={id} {...treatment} />;
              })}
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              sx={{ mt: 5, alignItems: "center" }}
            >
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: 500,
                  color: darkMode
                    ? getColors.drawerTextDark
                    : getColors.thumbnailTextLight,
                }}
              >
                Exercises
              </Typography>
              <Button
                variant="contained"
                startIcon={
                  <ChangeCircleOutlinedIcon
                    style={{ fontSize: 28, marginRight: 8 }}
                  />
                }
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "20px",
                  px: 4,
                  py: 1,
                  background: darkMode ? "#2D2E30" : "#E6E6E6",
                  color: darkMode
                    ? getColors.primaryTextDark
                    : getColors.secondaryTextLight,
                  "&:hover": {
                    color: getColors.primaryTextDark,
                  },
                }}
                onClick={handleExerciseClicked}
              >
                Change
              </Button>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={2}
              alignItems={"center"}
              sx={{ mt: 3 }}
            >
              {exercises.map((exercise, index) => (
                <ExerciseOriginItem key={index} {...exercise} />
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
            <Button
              sx={{
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 500,
                color: darkMode
                  ? getColors.appBarBgLight
                  : getColors.appBarBgDark,
                px: 6,
                py: "11px",
                textAlign: "center",
                "&: hover": {
                  background: "none",
                  color: getColors.primaryMain,
                },
                mr: 2,
              }}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 500,
                px: 6,
                py: "11px",
                textAlign: "center",
              }}
            >
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <SelectImageDialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        onSelectImage={handleImageSelect}
      />
      <EditTreatmentsDialog
        open={openTreatmentsDialog}
        onClose={() => setOpenTreatmentsDialog(false)}
        token={token}
        selectedTreatmentIds={selectedTreatmentIds}
        onSave={handleSaveTreatments}
      />
      <EditExercisesDialog
        open={openExercisesDialog}
        onClose={() => setOpenExercisesDialog(false)}
        token={token}
        selectedExerciseIds={selectedExerciseIds}
        onSave={handleSaveExercises}
      />
    </>
  );
};
